import { useCallback, useState } from 'react';

import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useHouseholdChatContext } from '@/modules/household/chat/contexts/householdChat.context';
import { HouseholdChatProvider } from '@/modules/household/chat/contexts/HouseholdChat.provider';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { MessageStatus } from '@/types/schema';

import { SetupValidationQuestions } from './SetupValidationQuestions';

interface Props {
  householdId: string;
  householdName: string;
  conversationId?: string;
  state: 'completed' | 'preparing' | 'unavailable';
  onAnswersAccepted: (conversationId: string) => void;
}

/** Keep drafts and response subscriptions alive across modal close and setup phases. */
export function SetupValidationQuestionsHost(props: Props) {
  const canEdit = useCanEditHouseholdData();
  const { isOpen, target } = useTrowser(TrowserKey.ValidationQuestions);
  const openHere = isOpen && target === props.householdId;
  const [hasOpened, setHasOpened] = useState(false);
  if (openHere && canEdit && !hasOpened) setHasOpened(true);
  if ((!canEdit || !openHere) && !hasOpened) return null;
  return (
    <HouseholdChatProvider
      key={props.householdId}
      householdId={props.householdId}
      singleConversation={{ conversationID: props.conversationId ?? null }}
    >
      <ValidationConversation {...props} />
    </HouseholdChatProvider>
  );
}

function ValidationConversation(props: Props) {
  const chat = useHouseholdChatContext();
  const { conversationId } = chat;
  const { onAnswersAccepted } = props;
  const notifyAnswersAccepted = useCallback(() => {
    if (conversationId) onAnswersAccepted(conversationId);
  }, [conversationId, onAnswersAccepted]);
  const hasCompletedResponse = chat.messages.some(
    (message) =>
      message.groupedQuestionContent &&
      message.status === MessageStatus.Responded
  );

  let state = props.state;
  if (hasCompletedResponse) {
    state = 'completed';
  }
  return (
    <SetupValidationQuestions
      householdId={props.householdId}
      householdName={props.householdName}
      message={
        state === 'completed'
          ? undefined
          : (chat.pendingInteractiveMessage ?? undefined)
      }
      onAnswersAccepted={notifyAnswersAccepted}
      state={state}
    />
  );
}
